import type {
  FleetManagementTypes,
  MetricAggregation,
  MetricWithAggregations,
  MetricsRequest,
  MetricsRequestType,
  MetricsResponse,
  MetricsResult,
  SeriesSummary,
} from '@nx/state';

import { metricKey } from '../../shared/utils';
import { DeploymentDatabaseCharts } from '../charts/deployments/database-charts';
import { DeploymentDashboardCharts } from '../charts/deployments/deployment-dashboard-charts';
import { DeploymentInstanceCharts } from '../charts/deployments/instance-charts';
import { DeploymentResourcesCharts } from '../charts/deployments/resources-charts';
import type { ChartType, ChartWidgetPartialProps } from '../charts/fullstack-chart-props';
import { mapFleetMetrics, mapMetrics } from '../charts/mappers';
import type { MultiChartProps } from '../charts/types';
import { nonNullable } from './typeguards';
import { MetricSource } from './types';

export class FleetDbmsMetricSource extends MetricSource {
  constructor(
    tenantId: string,
    public deployment: FleetManagementTypes.DeploymentDetails,
    public aggregations: boolean,
    database?: string,
  ) {
    super(tenantId, deployment.dbms?.dbmsId ?? '', deployment.name, database);
  }

  getCharts(): Record<ChartType, MultiChartProps[]> {
    return {
      HOST: DeploymentResourcesCharts,
      INSTANCE: DeploymentInstanceCharts,
      DB: DeploymentDatabaseCharts,
      DASHBOARD: DeploymentDashboardCharts,
    };
  }

  isCmiAvailable(): boolean {
    return false;
  }

  getPills(chartProps: MultiChartProps, metrics: MetricsResult['metrics']): SeriesSummary[] {
    const chartPropsList = Array.isArray(chartProps) ? chartProps : [chartProps];
    const additionalSeriesNames = chartPropsList.flatMap((chartProp) => {
      return chartProp.additionalMetric
        ? [
            {
              id: chartProp.subtitle ?? chartProp.title,
              name: chartProp.seriesName ?? chartProp.title,
              displayName: chartProp.seriesName ?? chartProp.title,
            },
            {
              id: chartProp.additionalMetric.subtitle ?? chartProp.additionalMetric.title,
              name: chartProp.additionalMetric.seriesName ?? chartProp.additionalMetric.title,
              displayName: chartProp.additionalMetric.seriesName ?? chartProp.title,
            },
          ]
        : [];
    });
    const pills = [...additionalSeriesNames];
    const [chartProp] = chartPropsList;
    const dataSeries = chartProp && metrics?.[metricKey(chartProp)]?.dataSeries;
    const serverDisplayNames = new Map(
      this.deployment.dbms?.servers.map((server) => [server.address, server.displayName]),
    );
    pills.push(
      ...Object.keys(dataSeries ?? {}).map((id) => {
        let displayName = id;
        const maybeDisplayName = serverDisplayNames.get(id);
        if (maybeDisplayName !== undefined) {
          displayName = maybeDisplayName;
        }
        return {
          id,
          name: id,
          displayName: displayName,
        };
      }),
    );
    pills.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return pills;
  }

  createMetricsRequest(
    startTime: number,
    endTime: number,
    chartType: ChartType,
    chartProps: MultiChartProps[],
  ): { metricsRequest: MetricsRequest; type: MetricsRequestType } {
    const getMetricNames = (): MetricWithAggregations[] => {
      const allAggregations: MetricAggregation[] = ['MAX', 'AVG', 'MIN'];
      const maxOnlyAggregations: MetricAggregation[] = ['MAX'];
      const names = chartProps
        .flat()
        .flatMap((c) => [
          {
            metric: c.metricName,
            aggregations: c.aggregations ? c.aggregations : allAggregations,
          },
          c.referenceMetricConfig
            ? {
                metric: c.referenceMetricConfig.name,
                aggregations: c.referenceMetricConfig.aggregation
                  ? [c.referenceMetricConfig.aggregation]
                  : maxOnlyAggregations,
              }
            : undefined,
          c.additionalMetric
            ? {
                metric: c.additionalMetric.metricName,
                aggregations: c.aggregations ? c.aggregations : allAggregations,
              }
            : undefined,
        ])
        .filter(nonNullable);
      return names.filter((metric, pos) => names.findIndex((item) => item.metric === metric.metric) === pos);
    };

    const metricNames = getMetricNames();

    return {
      metricsRequest: {
        dbName: chartType === 'DB' ? this.database : undefined,
        tenantId: this.tenantId,
        deploymentId: this.deployment.deploymentId,
        name: metricNames.map((mn) => mn.metric),
        startTime: startTime,
        endTime: endTime,
        isServerGrouped: this.aggregations,
      },
      type: 'fleetMetrics',
    };
  }

  mapMetrics(
    metrics: MetricsResponse,
    chartProps: MultiChartProps[],
    seriesNameMapper?: (props: ChartWidgetPartialProps) => string,
  ): MetricsResult['metrics'] {
    return this.aggregations
      ? mapFleetMetrics(
          metrics,
          chartProps.flat(),
          Object.fromEntries(this.deployment.dbms?.servers.map(({ serverId, address }) => [serverId, address]) ?? []),
        )
      : mapMetrics(metrics, chartProps.flat(), this.name, seriesNameMapper);
  }
}
